import { NavLink, Outlet, useParams } from "react-router-dom";
import { Container, Typography } from "@/components/atoms";
import { PageHeader, PageHeaderV2 } from "@/components/organisms";
import { isAssistantAccountType } from "@/constants/accountTypes";
import { useOperator, useProfile } from "@/hooks";
import { useGetClientProfileQuery } from "@/redux/apis/clientProfile/clientProfileApi";
import { clsx } from "@/utils";
import { ManageClientProvider } from "./context";
import { getRemovedAccessBannerText, isClientAccessRemoved } from "./details/removedAccessBanner";

// Tabs for a regular managed client.
const CLIENT_TABS = [
  { to: "details", label: "Client Details" },
  { to: "favourites", label: "Favourites" },
  { to: "settings", label: "Settings" },
] as const;

// Tabs for an Executive Assistant / Travel Manager: the first tab is relabelled and the
// "EA's Clients" tab is inserted second, listing that EA's managed clients (view-only).
const EA_TABS = [
  { to: "details", label: "Client Details (EA)" },
  { to: "ea-clients", label: "EA's Clients" },
  { to: "favourites", label: "Favourites" },
  { to: "settings", label: "Settings" },
] as const;

// Operator "Manage Client" record shell, following the project detail pattern
// (cf. CompaniesView): page title + Back, then tab navigation + active panel.
// Reads `:clientId` and exposes it to panels via context; panels target the
// client with the clientId override (see baseQueryWithClient). Tabs per Figma 2246:4724.
export const ManageClient = () => {
  const { clientId = "" } = useParams<{ clientId: string }>();
  const operator = useOperator();
  const { dateFormat } = useProfile();
  // Deduped with the tabs' own profile queries (same RTK key). Drives read-only enforcement and
  // EA/TM tab selection (the managed client's account type is surfaced on the profile).
  const { data: profile } = useGetClientProfileQuery({ accountId: clientId });
  const isViewOnly = isClientAccessRemoved(profile?.status);
  // Executive Assistant / Travel Manager get the EA-specific tabs (incl. "EA's Clients").
  const isAssistant = isAssistantAccountType(profile?.account_metadata?.type);
  const tabs = isAssistant ? EA_TABS : CLIENT_TABS;

  return (
    <ManageClientProvider value={{ clientId, isViewOnly }}>
      {/* WHITE full-width sticky header (title + tab labels centered, tab SEPARATOR
          full-width via -mx). Body below is a full-width GRAY band so the gray reads
          clearly against the white header; content centered in both. */}
      <div className="sticky top-0 z-30 -mx-3 bg-white px-3 lg:-mx-5 lg:px-5">
        <Container className="w-full">
          <PageHeaderV2 name="Clients" skipCrumbs={1} showDivider={false} />
          <PageHeader.Actions className="mt-3" />
        </Container>
        <div className="-mx-3 border-b border-neutral-mid-gray lg:-mx-5">
          <Container className="w-full">
            <nav aria-label="Manage client sections" className="flex items-center">
              {tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  className={({ isActive }) =>
                    clsx(
                      "-mb-px flex h-9 items-center justify-center px-4 py-2 text-base font-semibold leading-5",
                      isActive ? "border-b-2 border-primary text-primary-dark" : "text-neutral-silver-gray"
                    )
                  }
                >
                  {tab.label}
                </NavLink>
              ))}
            </nav>
          </Container>
        </div>
        {isViewOnly && (
          // View-only banner (DEV-1177, Figma 6317-98006): full-width red, centered text, pins with the header.
          <div role="alert" className="-mx-3 bg-danger lg:-mx-5">
            <Container className="w-full">
              <Typography variant="paragraph" className="px-3 py-2.5 font-semibold text-white lg:px-5">
                {getRemovedAccessBannerText(operator.name, profile?.removed_at, dateFormat)}
              </Typography>
            </Container>
          </div>
        )}
      </div>
      {/* Full-width GRAY body band; content centered (Container) */}
      <div className="-mx-3 flex flex-1 flex-col bg-neutral-surface-gray px-3 lg:-mx-5 lg:px-5">
        <Container className="w-full">
          <div className="flex flex-1 flex-col pb-12">
            <Outlet />
          </div>
        </Container>
      </div>
    </ManageClientProvider>
  );
};
